import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  Collection,
  CollectionListItem,
  GenerateCollectionsPayload,
  GenerateCollectionsResponse,
  ManualCollectionPayload,
  ManualCollectionResponse,
  CollectionMediaData,
} from '../../types/collections.types';

export interface CollectionsState {
  collections: Collection[];
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
  generatedCollections: GenerateCollectionsResponse | null;
  generating: boolean;
  generateError: string | null;
  saving: boolean;
  saveError: string | null;
  deleting: boolean;
  deleteError: string | null;
  addingBundles: boolean;
  addBundlesError: string | null;
  bundlesAdded: number;
  totalBundles: number;
  themeTitle: string | null;
  themeDescription: string | null;
  generatePayload: GenerateCollectionsPayload | null; // Store user preferences
  // Media data indexed by collection_id for fast lookup
  collectionMedia: Record<string, CollectionMediaData>;
  mediaLoading: boolean;
  mediaError: string | null;
  // Regenerate state - track per collection
  regeneratingImage: Record<string, boolean>; // collection_id -> isRegenerating
  regeneratingVideo: Record<string, boolean>; // collection_id -> isRegenerating
  regenerateError: Record<string, string | null>; // collection_id -> error message
  // Sidebar selection
  selectedItemId: string | null;
}

const initialState: CollectionsState = {
  collections: [],
  loading: false,
  error: null,
  lastFetched: null,
  generatedCollections: null,
  generating: false,
  generateError: null,
  saving: false,
  saveError: null,
  deleting: false,
  deleteError: null,
  addingBundles: false,
  addBundlesError: null,
  bundlesAdded: 0,
  totalBundles: 0,
  themeTitle: null,
  themeDescription: null,
  generatePayload: null,
  collectionMedia: {},
  mediaLoading: false,
  mediaError: null,
  regeneratingImage: {},
  regeneratingVideo: {},
  regenerateError: {},
  selectedItemId: null,
};

const collectionsSlice = createSlice({
  name: 'collections',
  initialState,
  reducers: {
    // ----- Sidebar Selection -----
    setSelectedItemId: (state, action: PayloadAction<string | null>) => {
      state.selectedItemId = action.payload;
    },

    // ----- Manual Collection (reuses generating flag, feeds into existing bundle flow) -----
    manualCollectionRequest: (state, action: PayloadAction<ManualCollectionPayload>) => {
      console.log('📦 Slice: manualCollectionRequest reducer called', action.payload);
      state.generating = true;
      state.generateError = null;
      state.themeTitle = action.payload.theme_title || null;
      state.themeDescription = action.payload.theme_description || null;
    },

    /**
     * Trigger collections fetch
     * Dispatched by component to initiate API call
     */
    fetchCollectionsRequest: (state) => {
      state.loading = true;
      state.error = null;
    },

    /**
     * Handle successful collections fetch
     * Dispatched by saga on success
     */
    fetchCollectionsSuccess: (state, action: PayloadAction<Collection[]>) => {
      state.loading = false;
      state.collections = action.payload;
      state.error = null;
      state.lastFetched = Date.now();
    },

    /**
     * Handle collections fetch failure
     * Dispatched by saga on failure
     */
    fetchCollectionsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    /**
     * Clear collections data
     */
    clearCollections: (state) => {
      state.collections = [];
      state.error = null;
      state.lastFetched = null;
    },

    /**
     * Clear error state
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Trigger collections generation
     * Dispatched by component to initiate API call
     */
    generateCollectionsRequest: (state, action: PayloadAction<GenerateCollectionsPayload>) => {
      console.log('📦 Slice: generateCollectionsRequest reducer called', action.payload);
      state.generating = true;
      state.generateError = null;
      // Store theme information from payload
      state.themeTitle = action.payload.theme_title || null;
      state.themeDescription = action.payload.theme_description || null;
      // ✅ Store the entire payload to access user preferences (brand_context_enabled, add_as_a_collection, etc.)
      state.generatePayload = action.payload;
    },

    /**
     * Handle successful collections generation
     * Dispatched by saga on success
     */
    generateCollectionsSuccess: (state, action: PayloadAction<GenerateCollectionsResponse>) => {
      state.generating = false;
      state.generatedCollections = action.payload;
      state.generateError = null;
    },

    /**
     * Handle collections generation failure
     * Dispatched by saga on failure
     */
    generateCollectionsFailure: (state, action: PayloadAction<string>) => {
      state.generating = false;
      state.generateError = action.payload;
    },

    /**
     * Clear generated collections data
     */
    clearGeneratedCollections: (state) => {
      state.generatedCollections = null;
      state.generateError = null;
      state.themeTitle = null;
      state.themeDescription = null;
      state.generatePayload = null;
    },

    /**
     * Trigger collection save (add new collection)
     * Dispatched by component to initiate API call
     */
    saveCollectionRequest: (state, action: PayloadAction<any>) => {
      console.log('📦 Slice: saveCollectionRequest reducer called', action.payload);
      state.saving = true;
      state.saveError = null;
    },

    /**
     * Handle successful collection save
     * Dispatched by saga on success
     */
    saveCollectionSuccess: (state, action: PayloadAction<any>) => {
      state.saving = false;
      state.saveError = null;
      // Optionally refresh collections list
    },

    /**
     * Handle collection save failure
     * Dispatched by saga on failure
     */
    saveCollectionFailure: (state, action: PayloadAction<string>) => {
      state.saving = false;
      state.saveError = action.payload;
    },

    /**
     * Trigger collection edit (update existing collection)
     * Dispatched by component to initiate API call
     */
    editCollectionRequest: (state, action: PayloadAction<any>) => {
      console.log('📦 Slice: editCollectionRequest reducer called', action.payload);
      state.saving = true;
      state.saveError = null;
    },

    /**
     * Handle successful collection edit
     * Dispatched by saga on success
     */
    editCollectionSuccess: (state, action: PayloadAction<any>) => {
      state.saving = false;
      state.saveError = null;
      // Optionally refresh collections list
    },

    /**
     * Handle collection edit failure
     * Dispatched by saga on failure
     */
    editCollectionFailure: (state, action: PayloadAction<string>) => {
      state.saving = false;
      state.saveError = action.payload;
    },

    /**
     * Trigger collection delete
     * Dispatched by component to initiate API call
     */
    deleteCollectionRequest: (state, action: PayloadAction<{ pk_id: string }>) => {
      console.log('📦 Slice: deleteCollectionRequest reducer called', action.payload);
      state.deleting = true;
      state.deleteError = null;
    },

    /**
     * Handle successful collection delete
     * Dispatched by saga on success
     */
    deleteCollectionSuccess: (state, action: PayloadAction<{ pk_id: string }>) => {
      console.log('✅ Slice: deleteCollectionSuccess - Removing pk_id:', action.payload.pk_id);
      console.log('📊 Collections before delete:', state.collections.length);
      
      state.deleting = false;
      state.deleteError = null;
      
      // Remove the deleted collection from the state
      const beforeCount = state.collections.length;
      state.collections = state.collections.filter(
        collection => collection.pk_id !== action.payload.pk_id
      );
      const afterCount = state.collections.length;
      
      console.log('📊 Collections after delete:', afterCount);
      console.log('🔄 State updated:', beforeCount !== afterCount ? 'SUCCESS' : 'FAILED - Collection not found');
    },

    deleteCollectionFailure: (state, action: PayloadAction<string>) => {
      state.deleting = false;
      state.deleteError = action.payload;
    },

    /**
     * Trigger automatic bundle addition after generation
     * Dispatched automatically after generateCollectionsSuccess
     */
    addGeneratedBundlesRequest: (state, action: PayloadAction<GenerateCollectionsResponse>) => {
      console.log('📦 Slice: addGeneratedBundlesRequest - Auto-adding bundles for generated collections');
      state.addingBundles = true;
      state.addBundlesError = null;
      state.bundlesAdded = 0;
      state.totalBundles = action.payload.collections.length;
    },

    /**
     * Track progress of bundle addition
     */
    addGeneratedBundleProgress: (state, action: PayloadAction<number>) => {
      state.bundlesAdded = action.payload;
    },

    /**
     * Handle successful completion of all bundle additions
     */
    addGeneratedBundlesSuccess: (state) => {
      console.log('✅ All generated bundles added successfully');
      state.addingBundles = false;
      state.addBundlesError = null;
      // Keep generatedCollections for preview, but mark as saved
    },

    /**
     * Handle failure in bundle addition
     */
    addGeneratedBundlesFailure: (state, action: PayloadAction<string>) => {
      console.error('❌ Failed to add generated bundles:', action.payload);
      state.addingBundles = false;
      state.addBundlesError = action.payload;
    },

    /**
     * Trigger fetch of all user collection media
     */
    fetchUserCollectionMediaRequest: (state, action: PayloadAction<{ userId: string }>) => {
      console.log('📸 Slice: fetchUserCollectionMediaRequest for userId:', action.payload.userId);
      state.mediaLoading = true;
      state.mediaError = null;
    },

    /**
     * Handle successful user media fetch
     * Stores media data indexed by collection_id
     */
    fetchUserCollectionMediaSuccess: (state, action: PayloadAction<CollectionMediaData[]>) => {
      console.log('✅ Slice: fetchUserCollectionMediaSuccess - Received', action.payload.length, 'media records');
      state.mediaLoading = false;
      state.mediaError = null;
      
      // Index media data by collection_id for fast lookup
      const mediaMap: Record<string, CollectionMediaData> = {};
      action.payload.forEach((media) => {
        mediaMap[media.collection_id] = media;
      });
      state.collectionMedia = mediaMap;
    },

    /**
     * Handle user media fetch failure
     */
    fetchUserCollectionMediaFailure: (state, action: PayloadAction<string>) => {
      console.error('❌ Slice: fetchUserCollectionMediaFailure:', action.payload);
      state.mediaLoading = false;
      state.mediaError = action.payload;
    },

    /**
     * Update media status for a specific collection (from EventSource)
     */
    updateCollectionMedia: (state, action: PayloadAction<CollectionMediaData>) => {
      console.log('🔄 Slice: updateCollectionMedia for collection:', action.payload.collection_id);
      state.collectionMedia[action.payload.collection_id] = action.payload;
    },

    /**
     * Clear collection media data
     */
    clearCollectionMedia: (state) => {
      state.collectionMedia = {};
      state.mediaError = null;
    },

    /**
     * Trigger image regeneration for a collection
     */
    regenerateImageRequest: (state, action: PayloadAction<{ collectionId: string; userId: string; imageUrls: string[]; productType?: string }>) => {
      console.log('🎨 Slice: regenerateImageRequest for collection:', action.payload.collectionId);
      state.regeneratingImage[action.payload.collectionId] = true;
      state.regenerateError[action.payload.collectionId] = null;
      
      // Optimistically update media status to 'pending'
      if (state.collectionMedia[action.payload.collectionId]) {
        state.collectionMedia[action.payload.collectionId].image_status = 'pending';
      }
    },

    /**
     * Handle successful image regeneration
     */
    regenerateImageSuccess: (state, action: PayloadAction<{ collectionId: string }>) => {
      console.log('✅ Slice: regenerateImageSuccess for collection:', action.payload.collectionId);
      state.regeneratingImage[action.payload.collectionId] = false;
      state.regenerateError[action.payload.collectionId] = null;
    },

    /**
     * Handle image regeneration failure
     */
    regenerateImageFailure: (state, action: PayloadAction<{ collectionId: string; error: string }>) => {
      console.error('❌ Slice: regenerateImageFailure for collection:', action.payload.collectionId, action.payload.error);
      state.regeneratingImage[action.payload.collectionId] = false;
      state.regenerateError[action.payload.collectionId] = action.payload.error;
      
      // Update media status to 'failed' on error
      if (state.collectionMedia[action.payload.collectionId]) {
        state.collectionMedia[action.payload.collectionId].image_status = 'failed';
      }
    },

    /**
     * Trigger video regeneration for a collection
     */
    regenerateVideoRequest: (state, action: PayloadAction<{ collectionId: string; userId: string; productType?: string }>) => {
      console.log('🎬 Slice: regenerateVideoRequest for collection:', action.payload.collectionId);
      state.regeneratingVideo[action.payload.collectionId] = true;
      state.regenerateError[action.payload.collectionId] = null;
      
      // Optimistically update media status to 'pending'
      if (state.collectionMedia[action.payload.collectionId]) {
        state.collectionMedia[action.payload.collectionId].video_status = 'pending';
      }
    },

    /**
     * Handle successful video regeneration
     */
    regenerateVideoSuccess: (state, action: PayloadAction<{ collectionId: string }>) => {
      console.log('✅ Slice: regenerateVideoSuccess for collection:', action.payload.collectionId);
      state.regeneratingVideo[action.payload.collectionId] = false;
      state.regenerateError[action.payload.collectionId] = null;
    },

    /**
     * Handle video regeneration failure
     */
    regenerateVideoFailure: (state, action: PayloadAction<{ collectionId: string; error: string }>) => {
      console.error('❌ Slice: regenerateVideoFailure for collection:', action.payload.collectionId, action.payload.error);
      state.regeneratingVideo[action.payload.collectionId] = false;
      state.regenerateError[action.payload.collectionId] = action.payload.error;
      
      // Update media status to 'failed' on error
      if (state.collectionMedia[action.payload.collectionId]) {
        state.collectionMedia[action.payload.collectionId].video_status = 'failed';
      }
    },
  },
});

export const {
  setSelectedItemId,
  manualCollectionRequest,
  fetchCollectionsRequest,
  fetchCollectionsSuccess,
  fetchCollectionsFailure,
  clearCollections,
  clearError,
  generateCollectionsRequest,
  generateCollectionsSuccess,
  generateCollectionsFailure,
  clearGeneratedCollections,
  saveCollectionRequest,
  saveCollectionSuccess,
  saveCollectionFailure,
  editCollectionRequest,
  editCollectionSuccess,
  editCollectionFailure,
  deleteCollectionRequest,
  deleteCollectionSuccess,
  deleteCollectionFailure,
  addGeneratedBundlesRequest,
  addGeneratedBundleProgress,
  addGeneratedBundlesSuccess,
  addGeneratedBundlesFailure,
  fetchUserCollectionMediaRequest,
  fetchUserCollectionMediaSuccess,
  fetchUserCollectionMediaFailure,
  updateCollectionMedia,
  clearCollectionMedia,
  regenerateImageRequest,
  regenerateImageSuccess,
  regenerateImageFailure,
  regenerateVideoRequest,
  regenerateVideoSuccess,
  regenerateVideoFailure,
} = collectionsSlice.actions;

export default collectionsSlice.reducer;
